import styled, { css } from 'styled-components';

import { DotLoadingIndicator } from '@xing-com/dot-loading-indicator';
import {
  mediaConfined,
  mediaWide,
  zIndexBackgroundLayer1,
  zIndexContentLayer2,
} from '@xing-com/layout-tokens';
import { Link } from '@xing-com/link';
import {
  dropShadowM,
  motionEasingLeave,
  motionTimeM,
  motionTimeS,
  scale010,
  scale020,
  scale110,
  scale130,
  scale160,
  scale180,
  scale190,
  spaceXXS,
  xdlColorA11yBorder,
  xdlColorA11yBorderOnLight,
  xdlColorButtonDecline,
  xdlColorButtonDeclineHover,
  xdlColorButtonHighlight,
  xdlColorButtonHighlightHover,
  xdlColorControlFill,
  xdlColorControlFillInvert,
  xdlColorControlFillSecondary,
  xdlColorDisabledSoft,
  xdlColorDisabledStrong,
  xdlColorFocusRing,
  xdlColorHoverQuaternary,
  xdlColorModalBorder,
  xdlColorPrimaryButtonHover,
  xdlColorSelected,
  xdlColorText,
  xdlColorTextInvert,
  xdlColorTextOnDark,
  xdlColorTextOnLight,
  xdlColorXING,
} from '@xing-com/tokens';

import type {
  FloatingButtonSizes,
  FloatingButtonVariants,
} from './floating-button.types';

type FloatingButtonTProps = {
  $size?: FloatingButtonSizes;
  $sizeConfined?: FloatingButtonSizes;
  $sizeWide?: FloatingButtonSizes;
  $variant: FloatingButtonVariants;
};

type IconContainerTProps = {
  $size?: FloatingButtonSizes;
  $sizeConfined?: FloatingButtonSizes;
  $sizeWide?: FloatingButtonSizes;
};

type LoadingTProps = {
  $size?: FloatingButtonSizes;
  $sizeConfined?: FloatingButtonSizes;
  $sizeWide?: FloatingButtonSizes;
};

// *** Sizes ***
export const sizes = {
  small: css`
    height: ${scale160};
    width: ${scale160};
  `,
  medium: css`
    height: ${scale180};
    width: ${scale180};
  `,
  large: css`
    height: ${scale190};
    width: ${scale190};
  `,
};

// *** Variants ***
export const variants = {
  primary: css`
    color: ${xdlColorTextOnLight};

    &::after {
      background-color: ${xdlColorXING};
      border-color: ${xdlColorA11yBorderOnLight};
      border-style: solid;
      border-width: ${scale010};
    }

    &:visited {
      color: ${xdlColorTextOnLight};
    }

    &:hover,
    &:focus {
      color: ${xdlColorTextOnLight};

      &::after {
        background-color: ${xdlColorPrimaryButtonHover};
        border-color: ${xdlColorA11yBorderOnLight};
        border-style: solid;
        border-width: ${scale010};
      }
    }

    &:active {
      color: ${xdlColorTextInvert};

      &::after {
        background-color: ${xdlColorSelected};
        border: none;
      }
    }

    &:disabled {
      color: ${xdlColorDisabledStrong};

      &::after {
        background-color: ${xdlColorDisabledSoft};
        border: none;
      }
    }

    &[data-loading='true'] {
      color: ${xdlColorTextOnLight};

      /** The loading dots */
      & [data-xds='DotLoadingIndicator'] > div {
        background-color: ${xdlColorTextOnLight};
      }

      &::after {
        background-color: ${xdlColorXING};
        border-color: ${xdlColorA11yBorderOnLight};
        border-style: solid;
        border-width: ${scale010};
      }
    }
  `,
  strong: css`
    color: ${xdlColorTextInvert};

    &::after {
      background-color: ${xdlColorControlFillInvert};
      border: none;
    }

    &:visited {
      color: ${xdlColorTextInvert};
    }

    &:hover,
    &:focus {
      color: ${xdlColorTextInvert};

      &::after {
        background-color: ${xdlColorHoverQuaternary};
        border: none;
      }
    }

    &:active {
      color: ${xdlColorText};

      &::after {
        background-color: ${xdlColorControlFill};
        border-color: ${xdlColorModalBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }

    &:disabled {
      color: ${xdlColorDisabledStrong};

      &::after {
        background-color: ${xdlColorDisabledSoft};
        border: none;
      }
    }

    &[data-loading='true'] {
      color: ${xdlColorTextInvert};

      /** The loading dots */
      & [data-xds='DotLoadingIndicator'] > div {
        background-color: ${xdlColorTextInvert};
      }

      &::after {
        background-color: ${xdlColorSelected};
        border: none;
      }
    }
  `,
  plain: css`
    color: ${xdlColorText};

    &::after {
      background-color: ${xdlColorControlFill};
      border-color: ${xdlColorModalBorder};
      border-style: solid;
      border-width: ${scale010};
    }

    &:visited {
      color: ${xdlColorText};
    }

    &:hover,
    &:focus {
      color: ${xdlColorText};

      &::after {
        background-color: ${xdlColorControlFillSecondary};
        border-color: ${xdlColorModalBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }

    &:active {
      color: ${xdlColorTextInvert};

      &::after {
        background-color: ${xdlColorSelected};
        border: none;
      }
    }

    &:disabled {
      color: ${xdlColorDisabledStrong};

      &::after {
        background-color: ${xdlColorDisabledSoft};
        border: none;
      }
    }

    &[data-loading='true'] {
      color: ${xdlColorText};

      /** The loading dots */
      & [data-xds='DotLoadingIndicator'] > div {
        background-color: ${xdlColorText};
      }

      &::after {
        background-color: ${xdlColorControlFill};
        border-color: ${xdlColorModalBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }
  `,
  highlight: css`
    color: ${xdlColorTextOnDark};

    &::after {
      background-color: ${xdlColorButtonHighlight};
      border-color: ${xdlColorA11yBorder};
      border-style: solid;
      border-width: ${scale010};
    }

    &:visited {
      color: ${xdlColorTextOnDark};
    }

    &:hover,
    &:focus {
      color: ${xdlColorTextOnDark};

      &::after {
        background-color: ${xdlColorButtonHighlightHover};
        border-color: ${xdlColorA11yBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }

    &:active {
      color: ${xdlColorTextInvert};

      &::after {
        background-color: ${xdlColorSelected};
        border: none;
      }
    }

    &:disabled {
      color: ${xdlColorDisabledStrong};

      &::after {
        background-color: ${xdlColorDisabledSoft};
        border: none;
      }
    }

    &[data-loading='true'] {
      color: ${xdlColorTextOnDark};

      /** The loading dots */
      & [data-xds='DotLoadingIndicator'] > div {
        background-color: ${xdlColorTextOnDark};
      }

      &::after {
        background-color: ${xdlColorButtonHighlight};
        border-color: ${xdlColorA11yBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }
  `,
  decline: css`
    color: ${xdlColorTextOnDark};

    &::after {
      background-color: ${xdlColorButtonDecline};
      border-color: ${xdlColorA11yBorder};
      border-style: solid;
      border-width: ${scale010};
    }

    &:visited {
      color: ${xdlColorTextOnDark};
    }

    &:hover,
    &:focus {
      color: ${xdlColorTextOnDark};

      &::after {
        background-color: ${xdlColorButtonDeclineHover};
        border-color: ${xdlColorA11yBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }

    &:active {
      color: ${xdlColorTextInvert};

      &::after {
        background-color: ${xdlColorSelected};
        border: none;
      }
    }

    &:disabled {
      color: ${xdlColorDisabledStrong};

      &::after {
        background-color: ${xdlColorDisabledSoft};
        border: none;
      }
    }

    &[data-loading='true'] {
      color: ${xdlColorTextOnDark};

      /** The loading dots */
      & [data-xds='DotLoadingIndicator'] > div {
        background-color: ${xdlColorTextOnDark};
      }

      &::after {
        background-color: ${xdlColorButtonDecline};
        border-color: ${xdlColorA11yBorder};
        border-style: solid;
        border-width: ${scale010};
      }
    }
  `,
};

const iconSizes = {
  small: css`
    height: ${scale110};
    width: ${scale110};

    & > svg {
      height: ${scale110};
      width: ${scale110};
    }
  `,
  medium: css`
    height: ${scale130};
    width: ${scale130};

    & > svg {
      height: ${scale130};
      width: ${scale130};
    }
  `,
  large: css`
    height: ${scale130};
    width: ${scale130};

    & > svg {
      height: ${scale130};
      width: ${scale130};
    }
  `,
};

// *** Base ***
const baseButton = css`
  align-items: center;
  background-color: transparent;
  border-radius: 50%;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  margin: 0;
  padding: 0;
  position: relative;
  text-decoration: none;
  vertical-align: top;
  z-index: ${zIndexContentLayer2};

  &::after {
    background-color: transparent;
    border-radius: 50%;
    box-shadow: ${dropShadowM};
    box-sizing: border-box;
    content: '';
    height: 100%;
    inset-block-start: 0;
    inset-inline-start: 0;
    position: absolute;
    transition-duration: ${motionTimeM};
    transition-property:
      transform, width, height, inset-block-start, inset-inline-start,
      background-color;
    transition-timing-function: ${motionEasingLeave};
    width: 100%;
    z-index: ${zIndexBackgroundLayer1};
  }

  &:hover {
    &::after {
      height: calc(100% + ${scale020} * 2);
      inset-block-start: -${scale020};
      inset-inline-start: -${scale020};
      width: calc(100% + ${scale020} * 2);
    }
  }

  &:active {
    transform: scale(1.125);
    transition-duration: ${motionTimeS};
    transition-property: transform;
    transition-timing-function: ${motionEasingLeave};

    &::after {
      height: calc(100% - ${scale020} * 2);
      inset-block-start: ${scale020};
      inset-inline-start: ${scale020};
      transition-duration: ${motionTimeS};
      width: calc(100% - ${scale020} * 2);
    }
  }

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:disabled {
    cursor: default;
    pointer-events: none;

    &::after {
      background-color: transparent;
      user-select: none;
    }
  }
`;

// *** Icon ***
export const IconContainer = styled.span<IconContainerTProps>`
  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return iconSizes.small;
      case 'medium':
        return iconSizes.medium;
      case 'large':
        return iconSizes.large;
      default:
        return $size satisfies undefined;
    }
  }};

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${iconSizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${iconSizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaConfined} {
            ${iconSizes.large}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'small':
        return css`
          @media ${mediaWide} {
            ${iconSizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${iconSizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaWide} {
            ${iconSizes.large}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};
`;

export const FloatingButton = styled.button<FloatingButtonTProps>`
  ${baseButton};

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      case 'large':
        return sizes.large;
      default:
        return $size satisfies undefined;
    }
  }};

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaConfined} {
            ${sizes.large}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaConfined} {
            ${sizes.large}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'small':
        return css`
          @media ${mediaWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaWide} {
            ${sizes.large}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};

  ${({ $variant }) => {
    switch ($variant) {
      case 'strong':
        return variants.strong;
      case 'primary':
        return variants.primary;
      case 'plain':
        return variants.plain;
      case 'highlight':
        return variants.highlight;
      case 'decline':
        return variants.decline;
      default:
        return $variant satisfies undefined;
    }
  }};
`;

export const RouterFloatingButton = styled(Link)<FloatingButtonTProps>`
  ${baseButton};

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      case 'large':
        return sizes.large;
      default:
        return $size satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'small':
        return css`
          @media ${mediaWide} {
            ${sizes.small}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${sizes.medium}
          }
        `;
      case 'large':
        return css`
          @media ${mediaWide} {
            ${sizes.large}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};

  ${({ $variant }) => {
    switch ($variant) {
      case 'strong':
        return variants.strong;
      case 'primary':
        return variants.primary;
      case 'plain':
        return variants.plain;
      case 'highlight':
        return variants.highlight;
      case 'decline':
        return variants.decline;
      default:
        return $variant satisfies undefined;
    }
  }};
`;

const smallLoading = css`
  transform: translate(-50%, -50%) scale(0.75);
`;

const mediumLoading = css`
  transform: translate(-50%, -50%) scale(0.9);
`;

const largeLoading = css`
  transform: translate(-50%, -50%);
`;

export const Loading = styled(DotLoadingIndicator)<LoadingTProps>`
  inset-block-start: 50%;
  inset-inline-start: 50%;
  position: absolute;
  transform: translate(-50%, -50%);

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return smallLoading;
      case 'medium':
        return mediumLoading;
      case 'large':
        return undefined;
      default:
        return $size satisfies undefined;
    }
  }};

  ${({ $sizeConfined }) => {
    switch ($sizeConfined) {
      case 'small':
        return css`
          @media ${mediaConfined} {
            ${smallLoading}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaConfined} {
            ${mediumLoading}
          }
        `;
      case 'large':
        return css`
          @media ${mediaConfined} {
            ${largeLoading}
          }
        `;
      default:
        return $sizeConfined satisfies undefined;
    }
  }};

  ${({ $sizeWide }) => {
    switch ($sizeWide) {
      case 'small':
        return css`
          @media ${mediaWide} {
            ${smallLoading}
          }
        `;
      case 'medium':
        return css`
          @media ${mediaWide} {
            ${mediumLoading}
          }
        `;
      case 'large':
        return css`
          @media ${mediaWide} {
            ${largeLoading}
          }
        `;
      default:
        return $sizeWide satisfies undefined;
    }
  }};
`;
